import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { reportDegradedLoad } from './helpers/report-degraded-load';
import { throwErrorWithId } from '@pypestream/utils';
import { minimatch } from 'minimatch';
import { saveAs } from 'file-saver';

import {
  ProStudioEvents,
  ProStudioContext,
  BotVersionFilesPattern,
} from '../prostudio.xstate.types';
import {
  buildVersionPsIdCandidates,
  checkForErrors,
  createCsvBlob,
  downloadAgentManagerVersionArchive,
  downloadZip,
  fetchVersionFileContent,
  pickVersionPsId,
  readLegacyBotSolutionId,
} from '../../utils';
import { tableStore } from '../../stores/table-store';
import {
  captureAnalyticsEvent,
  classifyAnalyticsError,
  solutionAnalyticsProps,
  splitSupportErrorId,
  MAX_ANALYTICS_ERROR_MESSAGE_LENGTH,
} from '../../utils/analytics';

// customerName.botName.botVersion/
// ├── bot.zip
// ├── app.py
// ├── dist/
// │   └── requirements.json
// ├── stopwords.json
// ├── templates/
// |   ├── bot.csv
// |   └── bot-grouped.csv   (PSTUDIO-3443: only when the solution has groups)
// └── services/
//     └── *.py

const patternsForSearch: BotVersionFilesPattern[] = [
  'dist/requirements.json',
  'stopwords.json',
  'templates/bot.csv',
  'services/*.py',
  'app.py',
];

/**
 * The Pro Studio row id of `version`, which `getProStudioVersionData` keys on — not
 * the dotted file `versionId`. Which form that row uses is recorded on the row itself
 * (`isUsingUpdatedId`), so both candidates are asked for and the answer picked from
 * what comes back — see buildVersionPsIdCandidates. Resolved here rather than read
 * from `ctx.solutionVersionPsId` because a download can target any version, not just
 * the draft. For the draft it lands on the same value, so the table store uses the
 * in-memory rows; for any other version it fetches that version's nodes. `''` when
 * there is no candidate at all.
 */
const resolveVersionPsId = async (
  ctx: ProStudioContext,
  version: string
): Promise<string> => {
  const psIdCandidates = buildVersionPsIdCandidates({
    customerId: ctx.customerId,
    botName: ctx.botName,
    versionName: version,
    solutionVersions: ctx.solutionVersions,
  });

  const psKeys = [psIdCandidates.updated, psIdCandidates.legacy].filter(
    (key): key is string => Boolean(key)
  );

  const psKeysResponse = psKeys.length
    ? await urqlGql.getSolutionVersionPsKeys({ keys: psKeys })
    : null;

  return pickVersionPsId(
    psIdCandidates,
    psKeysResponse?.data?.tenancy_solution_versions_ps
  );
};

/**
 * The CSVs generated from the table to bundle alongside the version's own files.
 *
 * An Agent Manager v2 archive (`includeWorkflowCsv`) gets the workflow itself as
 * `templates/bot.csv`, plus the grouped CSV when the version has groups. A v2 repo
 * has no workflow file until the version is published (`templates/bot.pif` is
 * committed at the publish snapshot), so this CSV is the ONLY copy of the nodes in
 * the zip — not best-effort: a failure fails the download, rather than handing the
 * user an archive with no workflow and reporting success. An unresolved version id
 * fails too: the table store would otherwise fall back to whatever rows it holds.
 *
 * A legacy download takes `bot.csv` from Bot Manager, so only the grouped CSV is
 * generated, best-effort: whatever goes wrong, the download still produces the
 * solution's files.
 */
const buildGeneratedCsvFiles = async (
  ctx: ProStudioContext,
  {
    version,
    wholeSolution,
    includeWorkflowCsv,
  }: { version: string; wholeSolution: boolean; includeWorkflowCsv: boolean }
): Promise<{ name: string; data: string }[]> => {
  if (!wholeSolution) return [];

  if (includeWorkflowCsv) {
    const versionPsId = await resolveVersionPsId(ctx, version);
    if (!versionPsId) {
      throwErrorWithId(`Could not resolve the workflow of version ${version}`);
    }
    const { workflowCsv, groupedCsv } =
      await tableStore.buildDownloadCsvs(versionPsId);
    return [
      ...(workflowCsv
        ? [{ name: 'templates/bot.csv', data: workflowCsv }]
        : []),
      ...(groupedCsv
        ? [{ name: 'templates/bot-grouped.csv', data: groupedCsv }]
        : []),
    ];
  }

  try {
    const groupedCsv = await tableStore.buildGroupedCsvString(
      await resolveVersionPsId(ctx, version)
    );
    return groupedCsv
      ? [{ name: 'templates/bot-grouped.csv', data: groupedCsv }]
      : [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      '[downloadVersionFile] grouped CSV bundling failed (skipping):',
      e
    );
    // The ZIP still downloads, one file short, and the user is told nothing.
    reportDegradedLoad(e, 'download-version-file:grouped-csv', { version });
    return [];
  }
};

export const downloadVersionFile = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<void> => {
  if (event.type !== 'getVersionFile') {
    return;
  }

  const { botName, customerId, project } = ctx;
  const projectId = project?.id;
  const { version, files = 'all', format = 'zip' } = event;

  // NO PRODUCER TODAY, and that is worth stating rather than leaving a
  // dashboard to read a constant as a measurement: both production senders of
  // `getVersionFile` omit `files` — `components/table/csv-downloader.tsx`
  // (which also passes `format: 'csv'`) and `pages/versions.tsx`. So
  // `file_scope` is `'all'` and `file_patterns` is `undefined` on every real
  // event, and the `'subset'` branch exists for a caller no screen offers yet.
  // Kept (rather than dropped) because the invoke already accepts `files` and
  // a narrowing entry point would otherwise ship with no telemetry at all.
  const fileScope = files === 'all' ? 'all' : 'subset';
  const filePatterns = files === 'all' ? undefined : files.join(', ');

  /**
   * Where the download died. Advanced as the `try` below progresses, so the
   * single catch can tell four distinguishable stages apart instead of
   * collapsing them into one unbounded `error_message`.
   */
  type DownloadFailureStage =
    | 'guard'
    | 'version_fetch'
    | 'file_fetch'
    | 'empty'
    | 'zip';

  let failureStage: DownloadFailureStage = 'guard';

  // Best-effort for the failure capture below — undefined on a failure just
  // means the guard that would have set it never ran (e.g. missing
  // projectId), not a bug in the capture itself.
  //
  // The TAIL of the ZIP's dotted folder name, deliberately not the whole of
  // it: the archive is named `${ctx.customerId}.${botName}.${version}`, and
  // `ctx.customerId` is the customer's display NAME rather than an id (the
  // real one arrives as `customer_id` from `solutionAnalyticsProps`, out of
  // `projectProStudioSetting`). Sending the display name would make this the
  // first property in this app to put a customer's name into PostHog, on a
  // client that already identifies the user by email; the tail identifies the
  // archive just as well next to `customer_id`. Useful for support ("which
  // archive did they get"), NOT a stable join key — use `version` for
  // anything that must survive a rename.
  let downloadPath: string | undefined;

  // A closure rather than a constant: `downloadPath` is only known part-way
  // through the `try`, and both captures must read whatever it holds at the
  // moment they fire.
  const downloadAnalyticsProps = () => ({
    ...solutionAnalyticsProps(ctx),
    // Can be `null`: the event type declares `version: string | null` and
    // `components/table/csv-downloader.tsx` builds it as
    // `ctx.draft?.version || ctx.draftVersion || null`. On that path the
    // `Version is required` guard fires, so the failure event carries
    // `version_number: null` beside `download_path: undefined` and says
    // nothing about what was being downloaded — `failure_stage: 'guard'` is
    // what identifies the shape. Left as is: making the toolbar refuse to
    // send without a version is a change to the caller, not to this invoke.
    version_number: version,
    download_path: downloadPath,
    file_scope: fileScope,
    file_patterns: filePatterns,
    // The workflows page now saves a single CSV and the Versions page the ZIP,
    // both as `solution_downloaded`; without this a breakdown of that event
    // cannot tell the two apart.
    download_format: format,
  });

  try {
    // `throwErrorWithId` is a `const` arrow whose `never` sits on the arrow
    // rather than on the declaration (packages/utils/errors, 876bc35bfa), so
    // TypeScript does not treat a call to it as a control-flow terminator and
    // does not narrow. Without the `return` the compiler carries
    // `string | undefined` past this guard and reports TS2322 where
    // `projectId` reaches `getVersionById` and `fetchVersionFileContent`.
    // Unreachable at runtime — the helper does throw.
    if (!projectId) {
      throwErrorWithId('Project ID is required');
      return;
    }

    if (!botName) {
      throwErrorWithId('Bot Name is required');
      return;
    }

    if (!customerId) {
      throwErrorWithId('Customer ID is required');
      return;
    }

    const botId = `${customerId}.${botName}`;

    if (!event.version) {
      throwErrorWithId('Version is required');
      return;
    }

    const patterns: BotVersionFilesPattern[] =
      !files || files === 'all' ? patternsForSearch : files;

    const versionId = `${botId}.${version}`;
    downloadPath = `${botName}.${version}`;

    // A v2 solution has no Bot Manager counterpart, so `getVersionById` /
    // `getVersionFile` below — both keyed by the dotted
    // `<customer>.<bot>.<version>` id — 404 and the whole download fails. The Agent
    // Manager archive endpoint serves the version as the backend has it instead.
    const solutionId = ctx.selectedBot?.uniqueId;
    const isAgentManagerArchive = !readLegacyBotSolutionId(ctx) && !!solutionId;

    // The workflows page's "Download as CSV": one CSV file instead of the ZIP. The
    // grouped CSV (base columns + the Group columns) when the version has groups,
    // the plain workflow CSV otherwise, so grouping survives a re-import.
    if (format === 'csv') {
      failureStage = 'file_fetch';
      const versionPsId = await resolveVersionPsId(ctx, event.version);
      if (!versionPsId) {
        throwErrorWithId(
          `Could not resolve the workflow of version ${event.version}`
        );
      }
      const { workflowCsv, groupedCsv } =
        await tableStore.buildDownloadCsvs(versionPsId);
      const csv = groupedCsv ?? workflowCsv;
      if (!csv) {
        failureStage = 'empty';
        throwErrorWithId('Version has no workflow to download');
        return;
      }
      saveAs(createCsvBlob(csv), `${downloadPath}.csv`);
      captureAnalyticsEvent('solution_downloaded', downloadAnalyticsProps());
      return;
    }

    // PSTUDIO-3443: bundle a grouped CSV (base columns + Group Id/Name/Order/
    // Collapsed) next to the solution's own files so node grouping survives the
    // download → re-import round-trip, and for a v2 archive the workflow CSV itself
    // (PSTUDIO-4354). Only for a full solution download. The workflow CSV is the v2
    // archive's file content, so a failure building it is a `file_fetch` failure.
    if (isAgentManagerArchive) failureStage = 'file_fetch';
    const generatedCsvFiles = await buildGeneratedCsvFiles(ctx, {
      version: event.version,
      wholeSolution: !files || files === 'all',
      includeWorkflowCsv: isAgentManagerArchive,
    });

    if (isAgentManagerArchive && solutionId) {
      failureStage = 'file_fetch';
      await downloadAgentManagerVersionArchive({
        solutionId,
        versionName: event.version,
        zipName: versionId,
        extraFiles: generatedCsvFiles,
      });
      captureAnalyticsEvent('solution_downloaded', downloadAnalyticsProps());
      return;
    }

    failureStage = 'version_fetch';

    const versionDataResponse = await urqlGql.getVersionById({
      versionId,
      projectId,
    });

    checkForErrors(versionDataResponse);

    const existingFiles: string[] =
      versionDataResponse.data?.getVersionById?.data.files || [];

    const filePathesToDownload = existingFiles.filter((file) =>
      patterns.some((pattern) => minimatch(file, pattern))
    );

    // `Promise.all([])` RESOLVES, so without this an empty match list walks
    // all the way to a saved ZIP holding one empty folder and a
    // `solution_downloaded` event that reports it as a success. The `|| []`
    // above is the acknowledgement that the list can be empty; PSTUDIO-3038
    // (6fb4ede555) replaced the same silent-empty-output shape with a throw.
    if (filePathesToDownload.length === 0) {
      failureStage = 'empty';
      throwErrorWithId('Version has no downloadable files');
      return;
    }

    failureStage = 'file_fetch';

    // See `fetchVersionFileContent` for the dual-channel extraction contract:
    // published versions deliver content via `data.getVersionFile`, draft
    // versions via `error.graphQLErrors[0].extensions.internal`. The helper
    // resolves both transparently and throws if neither yields content, so
    // the resulting ZIP either contains real file bodies or `Promise.all`
    // rejects and `downloadZip` is never called. Together with the
    // empty-match guard above, that is what rules out a silent empty archive.
    const downloadedfiles = await Promise.all(
      filePathesToDownload.map(async (filePath) => ({
        name: filePath,
        data: await fetchVersionFileContent({ projectId, versionId, filePath }),
      }))
    );

    downloadedfiles.push(...generatedCsvFiles);

    failureStage = 'zip';

    await downloadZip(versionId, downloadedfiles);
  } catch (error) {
    const { message, errorId } = splitSupportErrorId(
      error instanceof Error ? error.message : String(error)
    );
    const sentMessage = message.slice(0, MAX_ANALYTICS_ERROR_MESSAGE_LENGTH);

    captureAnalyticsEvent('solution_download_failed', {
      ...downloadAnalyticsProps(),
      failure_stage: failureStage,
      error_kind: classifyAnalyticsError(sentMessage),
      // The support ID travels in `error_id`, not inside the message: every
      // path here throws through `throwErrorWithId`, whose 92-character
      // suffix carries a fresh uuid, so leaving it in `error_message` makes
      // every event unique and a "why do downloads fail" breakdown a list of
      // rows with count 1. Splitting it first also means the truncation bound
      // is spent on the backend's own text rather than on the uuid.
      error_message: sentMessage,
      error_id: errorId,
    });
    throw error;
  }

  // Exactly one of the two captures fires, and the lines that make that true
  // are nameable: the `catch` above ends in `throw error;`, so this line is
  // unreachable on failure; and nothing on it can raise into that `catch`
  // anyway — `captureAnalyticsEvent` wraps its whole body in `try/catch`
  // (`utils/analytics.ts`) and `solutionAnalyticsProps` only optional-chains.
  //
  // The limitation, stated rather than implied: `downloadZip` ends at
  // `saveAs`, which returns `void` and does no more than dispatch a click on
  // an `<a download>`. A cancelled save dialog, a blocked download or a full
  // disk are all invisible here. `solution_downloaded` means "the archive was
  // handed to the browser", never "the user has a file".
  captureAnalyticsEvent('solution_downloaded', downloadAnalyticsProps());
};
